import { Clipboard } from '@capacitor/clipboard';
import { IonCol, IonGrid, IonPopover, IonRow, IonText } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import ActionIconButton from '@components/buttons/ActionIconButton';
import DateStamp from '@components/dates/DateStamp';
import styles from '@components/ExpertEmployer.module.css';
import Nbsp from '@components/Nbsp';
import HugeText from '@components/text/HugeText';
import LargeText from '@components/text/LargeText';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { EligibilityMethodEnum, GetEmployerWithBenefitsQuery } from '@typing/Generated';
import { countryNameForCode } from '@utils/countryUtils';
import { contractDetailsOnDate } from '@utils/employerContractUtils';
import { sortByStringField } from '@utils/modelUtils';
import { shortenWebsite } from '@utils/stringUtils';
import { minutesToHoursAndMinutes } from '@utils/timeUtils';

type PopoverState = {
  event: Event | undefined;
  open: boolean;
};

type DataItemProps = {
  isLink?: boolean;
  labelKey: string;
  value: string | null;
};

const DataItem = ({ isLink = false, labelKey, value }: DataItemProps) => {
  if (value) {
    return (
      <IonRow>
        <IonCol>
          <IonText className={styles.label}>
            <FormattedMessage id={`pages.expertJourney.employer.${labelKey}`} />
          </IonText>
          <Nbsp />
          {isLink ? <a href={value}>{value}</a> : value}
        </IonCol>
      </IonRow>
    );
  }
  return null;
};

type Employer = GetEmployerWithBenefitsQuery['employer'];

type Props = {
  employer: Employer;
};

const ExpertEmployer = ({ employer }: Props) => {
  const intl = useIntl();
  const [popoverState, setPopoverState] = useState<PopoverState>({ event: undefined, open: false });

  const handleCopyToClipboard = useCallback(
    (event: Event) => {
      Clipboard.write({ string: employer.enrollmentCode ?? '' }).then(() => {
        setPopoverState({ event, open: true });
      });
    },
    [employer.enrollmentCode]
  );

  useEffect(() => {
    if (popoverState.open) {
      const popoverTimeout = setTimeout(() => setPopoverState({ event: undefined, open: false }), 1500);
      return () => {
        clearTimeout(popoverTimeout);
      };
    }
  }, [popoverState]);

  const countries = useMemo(() => {
    if (employer) {
      const countries = employer.employeeCountByCountries.map(country => ({
        count: country.count,
        country: countryNameForCode(country.countryCode)
      }));
      return sortByStringField(countries, country => country.country);
    }
    return [];
  }, [employer]);

  const currentContractData = useMemo(() => {
    if (employer.currentContract) {
      return contractDetailsOnDate(employer.currentContract, new Date());
    }
  }, [employer.currentContract]);

  const sanitizedCulture = useSanitizeHtml(employer.culture, null, employer);

  return (
    <>
      <IonPopover
        className={styles.popover}
        event={popoverState.event}
        isOpen={popoverState.open}
        onDidDismiss={() => setPopoverState({ event: undefined, open: false })}
      >
        <div className={styles.popoverInside}>
          <FormattedMessage id="pages.expertJourney.employer.copied" />
        </div>
      </IonPopover>

      <>
        <div className={styles.employer}>
          <IonGrid>
            {employer.eligibilityMethod === EligibilityMethodEnum.ENROLLMENT_CODE && (
              <IonRow>
                <IonCol>
                  <LargeText>
                    <FormattedMessage id="pages.expertJourney.employer.enrollmentCode" />
                    <span className={styles.enrollmentCode}>
                      {employer.enrollmentCode ?? intl.formatMessage({ id: 'dictionary.none' })}
                      {employer.enrollmentCode && (
                        <ActionIconButton
                          ariaLabelKey="accessibility.ariaLabels.copy"
                          icon="clone"
                          onClick={event => {
                            handleCopyToClipboard(event as unknown as Event);
                          }}
                        />
                      )}
                    </span>
                  </LargeText>
                </IonCol>
              </IonRow>
            )}
            <DataItem
              labelKey="industry"
              value={
                employer.industry?.other
                  ? employer.industry.other
                  : employer.industry?.industryOption
                  ? intl.formatMessage({
                      id: `models.employer.industry.${employer?.industry?.industryOption}`
                    })
                  : null
              }
            />
            <IonRow>
              <IonCol>
                <IonText className={styles.label}>
                  <FormattedMessage id="pages.expertJourney.employer.contractStart" />
                </IonText>
                <Nbsp />
                {employer.currentContract?.startDate ? (
                  <DateStamp absoluteDate value={employer.currentContract?.startDate} />
                ) : (
                  <FormattedMessage id="dictionary.none" />
                )}
                <span className={styles.disc}>
                  <FormattedMessage id="dictionary.punctuation.circle" />
                </span>
                <IonText className={styles.label}>
                  <FormattedMessage id="pages.expertJourney.employer.contractEnd" />
                </IonText>
                <Nbsp />
                {employer.currentContract?.endDate ? (
                  <DateStamp absoluteDate value={employer.currentContract.endDate} />
                ) : (
                  <FormattedMessage id="dictionary.none" />
                )}
              </IonCol>
            </IonRow>
            <DataItem
              labelKey="expectedUtilization"
              value={employer.expectedUtilization ? intl.formatNumber(employer.expectedUtilization) : null}
            />
            <DataItem
              labelKey="globalPopulation"
              value={
                employer.globalPopulation
                  ? intl.formatMessage({ id: 'dictionary.yes' })
                  : employer.globalPopulation === false
                  ? intl.formatMessage({ id: 'dictionary.no' })
                  : null
              }
            />
            <DataItem isLink labelKey="website" value={employer.website} />
            <DataItem labelKey="address" value={employer.address} />
            {employer.culture && (
              <IonRow>
                <IonCol>
                  <IonText className={styles.label}>
                    <FormattedMessage id="pages.expertJourney.employer.culture" />
                  </IonText>
                  <Nbsp />
                  {sanitizedCulture}
                </IonCol>
              </IonRow>
            )}
            <DataItem
              labelKey="billableCalculation"
              value={
                currentContractData?.billableCalculation
                  ? intl.formatMessage({
                      id: `models.employerContract.billableCalculation.${currentContractData.billableCalculation}`
                    })
                  : null
              }
            />
            <DataItem
              labelKey="eligibilityMethod"
              value={
                employer.eligibilityMethod
                  ? intl.formatMessage({
                      id: `models.employer.eligibilityMethod.${employer.eligibilityMethod}`
                    })
                  : null
              }
            />
            <DataItem
              labelKey="benefitsHidden"
              value={
                employer.hideBenefits
                  ? intl.formatMessage({
                      id: `dictionary.yes`
                    })
                  : intl.formatMessage({
                      id: `dictionary.no`
                    })
              }
            />
            <div className={styles.infoArea}>
              <IonGrid>
                <IonRow>
                  <IonCol className={styles.infoBox}>
                    <div className={styles.bigNumber}>
                      {currentContractData?.hasCarePlanning ? (
                        <FormattedMessage
                          id="pages.expertJourney.employer.carePlanPlusHours"
                          values={{
                            hours: minutesToHoursAndMinutes({
                              minutes: currentContractData?.minutes ?? 0
                            })
                          }}
                        />
                      ) : (
                        <FormattedMessage
                          id="pages.expertJourney.employer.hoursPurchased"
                          values={{
                            hours: minutesToHoursAndMinutes({
                              minutes: currentContractData?.minutes ?? 0
                            })
                          }}
                        />
                      )}
                    </div>
                  </IonCol>
                  <IonCol className={styles.infoBox}>
                    <div className={styles.massiveNumber}>
                      <FormattedNumber value={employer.employeeCount ?? 0} />
                    </div>
                    <div className={styles.infoCaption}>
                      <FormattedMessage id="pages.expertJourney.employer.fullTimeEmployees" />
                    </div>
                  </IonCol>
                  <IonCol className={styles.infoBox}>
                    <div className={styles.massiveNumber}>
                      <FormattedNumber value={employer.clientCount ?? 0} />
                    </div>
                    <div className={styles.infoCaption}>
                      <FormattedMessage id="pages.expertJourney.employer.registeredClients" />
                    </div>
                  </IonCol>
                  <IonCol className={styles.infoBox}>
                    <div className={styles.massiveNumber}>
                      <FormattedNumber value={employer.currentRefreshPeriod?.billableClientCount ?? 0} />
                    </div>
                    <div className={styles.infoCaption}>
                      <FormattedMessage
                        id="pages.expertJourney.employer.billableClientsOfPurchased"
                        values={{ purchased: currentContractData?.employeesPurchased ?? 0 }}
                      />
                    </div>
                  </IonCol>
                </IonRow>
                <IonRow>
                  <IonCol className={styles.infoBox}>
                    <div className={styles.countryHeading}>
                      <HugeText>
                        <FormattedMessage id="pages.expertJourney.employer.employeeCountByCountries" />
                      </HugeText>
                    </div>
                    <div>
                      {countries.map(country => (
                        <div key={country.country}>
                          <LargeText>
                            {country.country}
                            <FormattedMessage
                              id="models.employer.employeeCount"
                              values={{ count: country.count ?? '' }}
                            />
                          </LargeText>
                        </div>
                      ))}
                    </div>
                  </IonCol>
                </IonRow>
              </IonGrid>
            </div>
            <IonRow className={styles.benefitsSection}>
              <IonCol>
                <HugeText>
                  <FormattedMessage id="pages.expertJourney.employer.benefits" />
                </HugeText>
              </IonCol>
            </IonRow>
            {employer.employerBenefits?.map(benefit => (
              <IonRow className={styles.benefitBox} key={benefit.id}>
                <IonCol>
                  <IonGrid>
                    <IonRow>
                      <IonCol>
                        <HugeText>{benefit.vendorName}</HugeText>
                      </IonCol>
                    </IonRow>
                    <IonRow className={styles.benefitRow}>
                      <IonCol>
                        <div className={styles.benefitHeading}>
                          <LargeText>
                            <FormattedMessage id="pages.expertJourney.employer.links" />
                          </LargeText>
                        </div>
                        {benefit.links.map(link => (
                          <div key={link}>
                            <a href={link} rel="noopener noreferrer" target="_blank">
                              {shortenWebsite(link)}
                            </a>
                          </div>
                        ))}
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCol>
              </IonRow>
            ))}
          </IonGrid>
        </div>
      </>
    </>
  );
};

export default ExpertEmployer;
